<template>
  <transition name="fade" :duration="500">
    <video-box
        v-if="boxComponent === 'video'"
        :key="taskKey"
        :content-url="mediaUrl"
        :content-srcset="mediaSrcset"
        :end-url="taskData.end_url || ''"
        :start-url="taskData.start_url || ''"
        :qr-position="taskData.qr_position"
        :qr-link="taskData.url + '?qr'"
        :task-id="taskData.t_id || null"
        :fail-url="taskData.fail_url || ''"
        @finished="onMediaFinished"
    />
    <one-third-banner-box
        v-if="boxComponent === 'one-third'"
        :key="taskKey"
        :content-url="mediaUrl"
        :content-srcset="mediaSrcset"
        :is-video="isOneThirdVideo"
        :confetti-enabled="confettiEnabled"
        :end-url="taskData.end_url || ''"
        :start-url="taskData.start_url || ''"
        :direction="oneThirdDirection"
        :duration="taskData.duration_seconds || 15"
        :qr-position="taskData.qr_position"
        :qr-link="taskData.url + '?qr'"
        :task-id="taskData.t_id || null"
        :fail-url="taskData.fail_url || ''"
        @finished="onMediaFinished"
    />
    <full-screen-banner-box
        v-if="boxComponent === 'full-screen'"
        :key="taskKey"
        :content-url="mediaUrl"
        :universal-settings="taskData.settings?.extensionUniversalJson || {}"
        :end-url="taskData.end_url || ''"
        :start-url="taskData.start_url || ''"
        :duration="taskData.duration_seconds || 15"
        :task-id="taskData.t_id || null"
        :fail-url="taskData.fail_url || ''"
        @finished="onMediaFinished"
    />
    <burger-king-animation
        ref="anim"
    />
  </transition>
</template>

<script>
import VideoBox from '../components/VideoBox'
import OneThirdBannerBox from '../components/OneThirdBannerBox'
import FullScreenBannerBox from '../components/FullScreenBannerBox'
import BurgerKingAnimation from '../components/BurgerKingAnimation'
import layerMixin from '../mixins/layer'
import { get } from 'lodash'
import constants from "@/constants"
import { reportAdFailure, reportAdReceived } from '../services/adFailure'
import { ruUrl } from '../services/region'
import { createLaunchPoller } from '../services/launchPoll'
import { createLaunchLedger } from '../services/launchLedger'

export default {
  name: 'VideoPage',
  mixins: [layerMixin],
  components: {
    VideoBox,
    OneThirdBannerBox,
    FullScreenBannerBox,
    BurgerKingAnimation
  },
  data () {
    return {
      // Events
      taskLaunchEvent: constants.EVENTS.TASK_LAUNCH,
      taskTestModeEvent: constants.EVENTS.TASK_TEST_MODE,
      bkClickedEvent: constants.EVENTS.BK_CLICKED,

      pusher: null,
      channels: {},
      taskData: null,
      kickLaunchTimeout: null,
      // The events those two timers are holding. Kept so that a launch replaced
      // by a newer one can be named in the report instead of vanishing.
      kickPendingEvent: null,
      pendingLaunchEvent: null,
      // Not reactive on purpose: handles, not state the template reads.
      launchPoller: null,
      launchLedger: null
    }
  },
  computed: {
    confettiEnabled () {
      // TaskExecuted sends a boolean per launch; older backends omit the field.
      return this.taskData?.confetti_enabled !== false
    },
    mediaUrl () {
      const url = this.taskData?.content_url || ''
      return this.transformUrl(url)
    },

    mediaSrcset () {
      const set = this.taskData?.content_srcset
      if (!set) return {}
      const out = {}
      for (const [type, url] of Object.entries(set)) {
        out[type] = this.transformUrl(url)
      }
      return out
    },

    boxComponent () {
      if (!this.taskData?.content_url) {
        return null
      }

      const { content_type, settings } = this.taskData

      if (constants.VIDEO_CONTENT_TYPES.includes(content_type)) return constants.COMPONENT_TYPES.VIDEO
      if (content_type === constants.CONTENT_TYPES.ONE_THIRD_BANNER) return constants.COMPONENT_TYPES.ONE_THIRD

      if (content_type === constants.CONTENT_TYPES.EXTENSION_UNIVERSAL && settings) {
        return settings.template === constants.TEMPLATE_VALUES.FULL_SCREEN_TEMPLATE
            ? constants.COMPONENT_TYPES.FULL_SCREEN
            : constants.COMPONENT_TYPES.ONE_THIRD
      }

      return null
    },

    oneThirdDirection () {
      if (!this.taskData?.content_url) {
        return null
      }

      const { content_type, settings } = this.taskData

      if (content_type === constants.CONTENT_TYPES.ONE_THIRD_BANNER) {
        return settings?.direction || null
      }

      if (content_type === constants.CONTENT_TYPES.EXTENSION_UNIVERSAL &&
          settings?.template !== constants.TEMPLATE_VALUES.FULL_SCREEN_TEMPLATE &&
          settings?.template !== undefined
      ) {
        return constants.DIRECTION_MAP[settings.template] || null
      }

      return null
    },

    isOneThirdVideo () {
      if (this.boxComponent !== constants.COMPONENT_TYPES.ONE_THIRD) {
        return false
      }

      const canvasMediaType = get(this.taskData, 'canvas_data.media[0].type')
      const mediaType = get(this.taskData, 'media_type')

      return canvasMediaType === constants.CONTENT_TYPES.VIDEO ||
          mediaType === constants.CONTENT_TYPES.VIDEO
    },

    // Unique key per launched task, so Vue destroys and remounts the ad
    // component cleanly every time instead of reusing the instance.
    // Reusing the instance would skip mounted() — .play(), event listeners,
    // and the playback watchdog would never run for ad #2+.
    taskKey () {
      if (!this.taskData) return null
      return this.taskData.launch_id != null
          ? `launch:${this.taskData.launch_id}`
          : this.taskData.t_uuid
          || this.taskData.t_id
          || `${this.taskData.execute_at || ''}:${this.taskData.content_url || ''}`
    }
  },

  methods: {
    transformUrl (url) {
      if (!url || !this.isRuVersion) return url

      return ruUrl(url)
    },

    onMediaFinished (key) {
      // A leaving component may finish asynchronously after its replacement mounted.
      // Vue 2 <transition> prefixes the $vnode.key emitted by each box.
      // Strip that prefix so the current ad unmounts and releases its progress
      // bar and reload guard, while a stale finish cannot clear a newer launch.
      const reported = String(key ?? '').replace(/^__transition-\d+-/, '')
      if (reported !== String(this.taskKey ?? '')) return
      this.taskData = null
    },

    getLaunchChannel () {
      return `task-launch.${this.userLink}`
    },

    /**
     * A launch that arrived and was never shown.
     *
     * VideoBox reports its own failures, but only once it has mounted; a launch
     * dropped in initTaskLaunch never gets that far and used to leave nothing at
     * all. That is the larger of the two holes: measured on twitch.tv/redpandash
     * over 02-04.09.2026, every launch that reached the ad component finished it -
     * 43 of 43, 23 of 23, 9 of 9 - while only about six in ten launches reached it.
     *
     * With this the three outcomes are told apart from the server alone. A start
     * report means the ad was mounted; a report from here means the event arrived
     * and was discarded, and says why; neither means it never arrived at all.
     */
    dropLaunch (reason, e, extra = {}) {
      if (!e) return

      reportAdFailure(reason, {
        failUrl: e.fail_url || '',
        taskId: e.t_id || null,
        contentUrl: e.content_url || null,
        ...extra
      })
    },

    initTaskLaunch() {
      this.on(this.getLaunchChannel(), this.taskLaunchEvent, (e) => this.handleLaunch(e, 'ws'))

      this.on(this.getLaunchChannel(), this.taskTestModeEvent, (e) => {
        this.taskData = e
      })
    },

    /**
     * One launch, from whichever path delivered it.
     *
     * `via` is 'ws' for the broadcast and 'poll' for the recovery. A launch_id seen
     * before is a repeat of one already taken - the socket and the poll are meant to
     * overlap - and is dropped before anything else, receipt included, so the backend
     * hears of a launch once.
     */
    handleLaunch (e, via) {
      if (e && e.launch_id != null) {
        if (!this.launchLedger) this.launchLedger = createLaunchLedger(window.localStorage)
        if (!this.launchLedger.claim(e.launch_id)) {
          console.log('launch already taken, ignoring', e.launch_id, via)
          return
        }
      }
      if (via === 'poll') console.warn('launch recovered by poll', e && e.launch_id)

      // First, before any decision about it. Everything below can end in the event
      // being ignored, and some of those paths are not ours to know about; this is
      // what tells a launch that arrived and vanished from one that never arrived.
      reportAdReceived(e, via)
      // Counted for the heartbeat, so a socket that is up and delivering is
      // distinguishable from one that is merely up. Counted for the poll as well:
      // it is a launch the layer received.
      this._launchEventsSeen = (this._launchEventsSeen || 0) + 1

      if (e.type === 'web') {
        console.log('got event', e)

        if (e.platform_id === 3) {
          console.log('it is kick, setting timeout')
          if (this.kickLaunchTimeout) {
            clearTimeout(this.kickLaunchTimeout)
            this.dropLaunch('superseded', this.kickPendingEvent)
          }
          this.kickPendingEvent = e
          this.kickLaunchTimeout = setTimeout(() => {
            this.kickLaunchTimeout = null
            this.kickPendingEvent = null
            this.taskData = e
            console.log('7 seconds passed, setting taskData')
          }, constants.KICK_PLATFORM_DELAY_MS)
        } else {
          console.log('it is not kick, setting taskData immediately')
          this.taskData = e
        }
      } else if (e.type === 'webWithExtensionContent') {
        // How much longer to wait, as the backend sees it. Both numbers come
        // from the same clock, so the difference between them is a duration
        // and not a moment - which is the whole point. Comparing execute_at
        // against this machine's Date.now() made the ad start late by exactly
        // however far the machine's clock was behind: measured on production
        // 26.08, 9 of 11 launches on this path began more than 25s after they
        // were scheduled, the worst 100s late, and always the same streamers.
        //
        // A payload without server_now is an older backend; fall back to the
        // clock and behave as before.
        const executeAtMs = e.execute_at * 1000
        const waitMs = e.server_now
          ? executeAtMs - e.server_now * 1000
          : executeAtMs - new Date().getTime()

        console.log('got event webWithExtensionContent', {
          executeAt: executeAtMs,
          serverNow: e.server_now ? e.server_now * 1000 : null,
          waitMs
        })

        // Still bounded, for a payload that makes no sense rather than for a
        // clock that is wrong: with server_now there is no skew left to
        // tolerate. Previously this window was 20s and it silently dropped
        // ads on machines whose clocks had drifted.
        const MAX_WAIT_MS = 5 * 60 * 1000
        if (waitMs > MAX_WAIT_MS) {
          console.log('execute_at is too far in the future (>5min), ignoring event')
          this.dropLaunch('too_far_ahead', e, { waitMs })
        } else if (waitMs <= 0) {
          console.log('execute_at has already passed, playing immediately')
          this.taskData = e
        } else {
          console.log('waiting', waitMs, 'ms for launch')
          if (this.waitForLaunchInterval) {
            clearInterval(this.waitForLaunchInterval)
            this.dropLaunch('superseded', this.pendingLaunchEvent)
          }
          this.pendingLaunchEvent = e

          // Date.now() is read twice and only ever subtracted from itself, so
          // an offset cancels out. It is a stopwatch here, not a calendar.
          const armedAt = new Date().getTime()
          this.waitForLaunchInterval = setInterval(() => {
            if (new Date().getTime() - armedAt >= waitMs) {
              console.log('launching', e)
              this.taskData = e
              clearInterval(this.waitForLaunchInterval)
              this.waitForLaunchInterval = null
              this.pendingLaunchEvent = null
            }
          }, 500)
        }
      } else {
        // Neither branch above claimed it. The two this layer knows are `web`
        // and `webWithExtensionContent`; `extension` and
        // `extensionOnlyClickableLayer` belong to the Twitch extension and are
        // meant to land here. Reported all the same, because a type that should
        // have been ours and is not looks, from the outside, exactly like one
        // that was never meant for us.
        console.log('unhandled launch type, ignoring event', e.type)
        this.dropLaunch('unknown_type', e)
      }
    },

    /**
     * The recovery path: ask the backend what is still current, so a launch the
     * socket missed is not lost with it. RU zone only for now - that is where the
     * losses are (05.10.2026: Twitch/ru 90.6% of launches started against 99.4% for
     * tr on the same broker). `?poll=1` forces it on and `?poll=0` off, for testing a
     * single streamer without a release.
     */
    initLaunchPolling () {
      const override = this.$route && this.$route.query ? this.$route.query.poll : undefined
      if (override === '0' || (override !== '1' && !this.isRuVersion)) return

      this.launchPoller = createLaunchPoller({
        url: `${this.backendHost}/player/events/${this.userLink}`,
        onLaunch: (e) => this.handleLaunch(e, 'poll')
      })
      this.launchPoller.start()

      // A socket that has just come back, or a launch channel that has just been
      // joined, is when a launch is most likely to have been missed.
      const pollNow = () => this.launchPoller && this.launchPoller.pollNow()
      try {
        this.pusher.connection.bind('connected', pollNow)
        this.channels[this.getLaunchChannel()].bind('pusher:subscription_succeeded', pollNow)
      } catch (err) {
        console.warn('[poll] could not hook the socket', err)
      }
      this._pollNow = pollNow
    },

    initBurgerKingClicksTracking() {
      this.on(this.getLaunchChannel(), this.bkClickedEvent, () => this.$refs.anim.play())
    },

    trySafeReload () {
      // A received launch waiting for its scheduled time already owns this page.
      if (this.boxComponent !== null || this.waitForLaunchInterval !== null ||
          this.kickLaunchTimeout !== null || this.pendingLaunchEvent || this.kickPendingEvent) return
      if (sessionStorage.getItem('needs_reload') !== '1') return
      sessionStorage.removeItem('needs_reload')
      location.reload()
    },

    clearLayerTimers () {
      if (this.waitForLaunchInterval) {
        clearInterval(this.waitForLaunchInterval)
        this.waitForLaunchInterval = null
      }
      if (this.kickLaunchTimeout) {
        clearTimeout(this.kickLaunchTimeout)
        this.kickLaunchTimeout = null
      }
      // Manual source teardown can still cancel a received launch. Report once,
      // while its signed fail_url is available; keepalive delivery is best effort.
      const pending = [this.pendingLaunchEvent, this.kickPendingEvent]
      this.pendingLaunchEvent = null
      this.kickPendingEvent = null
      pending.forEach(event => this.dropLaunch('layer_teardown', event))
    }
  },
  mounted () {
    // Start presence heartbeat (60s)
    this._hbLayer = 'video'
    this.startHeartbeat && this.startHeartbeat({
      screenId: this.userLink,
      layer: 'video',
      intervalMs: 60000
    })

    this.initPusher()
    this.initTaskLaunch()
    this.initLaunchPolling()
    this.initBurgerKingClicksTracking()

    this.enableWindowSizeTracking('video')

    const onVis = () => {
      if (!document.hidden) {
        this.trySafeReload()
        if (this._pollNow) this._pollNow()
      }
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('online', onVis)

    // OBS browser sources never fire visibilitychange events, so we also
    // poll periodically, using the same guard for scheduled and running ads.
    this._reloadPoll = setInterval(() => this.trySafeReload(), 10000)

    this._offVis = () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('online', onVis)
      if (this._reloadPoll) {
        clearInterval(this._reloadPoll)
        this._reloadPoll = null
      }
    }
  },
  beforeDestroy () {
    try { this.launchPoller && this.launchPoller.stop() } catch (e) { void e }
    try { this._offVis && this._offVis() } catch (e) { void e }
  }
}
</script>
